/* ==========================================================================
   SelectPanel > Client > Prototype
   ========================================================================== */

/* Import modules */
const { fromEvent } = require('rxjs');
const { filter } = require('rxjs/operators');

const { Store } = require('@bluekiri/alexandria/helpers/state');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { startUi } = require('./ui');
const { initInput } = require('./input');
const { initPanel, destroyPanel } = require('./panel');

const { getChoiceByDeafult, getUpdatedChoices } = require('../../helpers');

const { ESCAPE_CODE } = require('../../constants');

/* Prototype */
const prototype = {
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            data: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            setValue: value => this.store.dispatch(actions.setValue(value)),
            setOptions: options => this.store.dispatch(actions.setOptions(options)),
            changeOptions: options => {
                const { value } = getChoiceByDeafult({ items: options });
                return this.store.dispatch(actions.changeOptions({ items: options, value }));
            },
            changeValue: value => this.store.dispatch(actions.changeValue(value))
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Destroy components */
                destroyPanel(this);

                /* Unsubscribe */
                this.unsubscribe();
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());

                /* Clean DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean reference */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            changeOptions: this.store.subscribe((state, action) => {
                if (action.type !== types.CHANGE_OPTIONS) return;
                if (!this.input) return;

                /* Data store */
                const { value, items = [], panel } = this.store.select('data');

                /* Close panel */
                if (panel) this.actions.closePanel();

                /* Check value is valid */
                const { label: inputValue } = getChoiceByDeafult({ items, value });
                if (!inputValue) {
                    this.input.actions.reset();
                    return;
                }

                /* Sync value input */
                this.input.hooks.input.title = inputValue;
                this.input.actions.setValue(inputValue);
            }),
            changeValue: this.store.subscribe((state, action) => {
                if (action.type !== types.CHANGE_VALUE) return;
                if (!this.input) return;

                /* Data store */
                const { value, items = [] } = this.store.select('data');

                /* Get value by default */
                const { label: inputValue } = getChoiceByDeafult({ items, value });

                /* Reset input */
                if (inputValue === undefined || inputValue === null) {
                    this.input.actions.reset();
                    this.actions.changeOptions(items);
                    return;
                }

                /* Sync store items(select options) */
                const selectChoices = getUpdatedChoices({ items, value });
                this.actions.setOptions(selectChoices);

                /* Sync value input */
                this.input.hooks.input.title = inputValue;
                this.input.actions.setValue(inputValue);
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {};

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        /* Sync store */
        const { value, items = [] } = this.config || {};

        if (value) this.actions.setValue(value);
        if (items) this.actions.setOptions(items);

        return this;
    },

    /* Setup events */
    setupEvents() {
        if (this.events) return this;

        this.events = {
            keyup$: fromEvent(window.document, 'keyup').pipe(filter(event => event.keyCode === ESCAPE_CODE))
        };

        return this;
    },

    /* Init */
    init() {
        if (!this.config.init) return this;

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Setup events */
        this.setupEvents();

        /* Start */
        startUi(this);
        this.start();

        /* Init components */
        initInput(this);
        initPanel(this);

        return this;
    }
};

/* Exports */
module.exports = prototype;
